// engine-exception: portal home — tenant-configured menu tiles, welcome copy and the customer's own counts; a landing page, not a resource screen
import { Link } from 'react-router-dom';
import { Icon } from '../../engine/design/icons';
import { Badge, Stat } from '../../engine/design/primitives/Misc';
import { RichTextView } from '../../engine/fields/RichText';
import { formatDate } from '../../lib/format';
import { buttonHref, isExternal, safeIcon, usePortal } from '../PortalContext';
import { usePortalList } from '../hooks';
import type { ApprovalRow, TicketRow } from '../types';
import { ListState, StatusPill } from '../ui';

export function HomePage() {
  const { me, can } = usePortal();
  const tiles = me.menu_buttons.filter((b) => b.show_on_home).sort((a, b) => a.sort - b.sort);
  const recent = usePortalList<TicketRow>('tickets', { mine: 1 }, { limit: 5 });
  const approvals = usePortalList<ApprovalRow>('approvals', {}, { limit: 50, enabled: can('approvals.vote') });
  const pending = approvals.rows.filter((a) => a.state === 'pending' && !a.voted).length;
  const t = me.counts.tickets;
  const open = (t['open'] ?? 0) + (t['in_progress'] ?? 0);
  const first = me.contact.name.split(/\s+/)[0];

  return (
    <div className="hf-stack">
      <section className="pt-welcome">
        <div className="hf-caps">{me.client?.name ?? me.tenant.name}</div>
        <h1 style={{ margin: '4px 0 0' }}>Hello {first}</h1>
        {me.branding.welcome_html && <RichTextView html={me.branding.welcome_html} />}
      </section>

      <div className="hf-stats">
        <Stat label="Open tickets" value={open} icon="ticket" />
        <Stat label="Resolved" value={t['resolved'] ?? 0} icon="check-circle" />
        {can('approvals.vote') && <Stat label="Awaiting your approval" value={pending} icon="shield-check" accent={pending > 0} />}
        <Stat label="Unread" value={me.counts.unread_notifications} icon="bell" />
      </div>

      <nav className="pt-tiles" aria-label="Portal shortcuts">
        {tiles.map((b) => {
          const badge = b.function === 'approvals' && pending > 0 ? <Badge tone="warn" dot={false}>{pending}</Badge> : b.function === 'tickets' && open > 0 ? <Badge tone="info" dot={false}>{open}</Badge> : null;
          const body = (
            <>
              <div className="hf-row"><span className="ico"><Icon name={safeIcon(b.icon, 'circle')} size={18} /></span>{badge && <span className="badge">{badge}</span>}</div>
              <b>{b.label}</b>
              {b.description && <small>{b.description}</small>}
            </>
          );
          return isExternal(b) ? (
            <a key={b.id} className="pt-tile" href={b.route} target="_blank" rel="noopener noreferrer">{body}</a>
          ) : (
            <Link key={b.id} className="pt-tile" to={buttonHref(b)}>{body}</Link>
          );
        })}
      </nav>

      <section>
        <h2 className="hf-section-title hf-between">Your recent tickets <Link to="/portal/tickets" className="hf-link" style={{ fontSize: 'var(--t-12)' }}>All tickets</Link></h2>
        <ListState loading={recent.isLoading} error={recent.error} count={recent.rows.length} empty={{ title: 'No tickets yet', hint: can('tickets.create') ? <Link to="/portal/tickets/new">Log your first ticket</Link> : undefined, icon: 'ticket' }}>
          <div className="pt-cards">
            {recent.rows.map((r) => (
              <Link key={r.id} to={`/portal/tickets/${r.id}`} className="pt-card">
                <span className="ref">{r.ref}</span>
                <span className="title">{r.summary}</span>
                <span className="meta"><span>{r.type_name}</span><span>Updated {formatDate(r.updated_at)}</span></span>
                <span className="side"><StatusPill status={r.status_category} label={r.status_name} kind="ticket" /></span>
              </Link>
            ))}
          </div>
        </ListState>
      </section>
    </div>
  );
}
